import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, Upload } from 'lucide-react';
import { Badge, Button, Dialog, Spinner, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import type { LayoutRack } from '../../warehouse/api/warehouse.api.js';
import { rackLabel } from '../../warehouse/ui/RackFacade.js';
import { useWarehouseLayout } from '../../warehouse/model/warehouse.queries.js';

/** Javon shu foizdan to'lgan bo'lsa ogohlantirish belgisi chiqadi. */
const CROWDED = 90;

export interface PlaceConfirmItem {
  lineId: string;
  cellId: string;
  qty: number;
  /** Ekranda ko'rsatish uchun — API ga ketmaydi. */
  code: string;
  name: string;
  unit: string;
}

export interface PlaceConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  warehouseId: string;
  items: readonly PlaceConfirmItem[];
  loading: boolean;
  /** Serverdan kelgan xato — oyna ochiq qoladi va tanlov saqlanadi. */
  error?: string | null;
}

interface RackGroup {
  rackId: string;
  label: string;
  percent: number;
  cells: string[];
}

/**
 * YUKLASHNI TASDIQLASH.
 *
 * Ilgari yakunlash tugmasi bosilishi bilan `POST` ketardi: nima
 * yuborilayotgani ko'rinmasdi va xato faqat toast bo'lib o'tardi.
 * Joylashtirish esa QAYTARILMAYDIGAN amal — ombor qoldig'i o'zgaradi
 * va kirim hujjatlari yoziladi.
 *
 * Oyna JAVON tanlamaydi. Shartnoma har joylashtirish uchun `cellId` ni
 * talab qiladi (`supplyPlacementSchema`) va katak allaqachon javon
 * chizmasidan tanlangan. Bitta javonda o'nlab katak bor — dastur
 * ularning qay birini tanlashini o'zi hal qilsa, mahsulot noto'g'ri
 * o'ringa tushardi. Shuning uchun oyna tanlanganini KO'RSATADI:
 * qaysi javon, qaysi kataklar va o'sha javon qanchalik to'lgan.
 */
export const PlaceConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  warehouseId,
  items,
  loading,
  error = null,
}: PlaceConfirmDialogProps) => {
  const [listOpen, setListOpen] = useState(false);
  // So'rov FAQAT oyna ochilganda ketadi; javob 60 soniya keshda turadi
  // va joylashtirish ekrani bilan bir manbadan olinadi.
  const layout = useWarehouseLayout(open ? warehouseId : null);

  /* Yuklash davomida sahifadan chiqib ketish — yarim yozilgan hujjat.
     Brauzer o'z matnini ko'rsatadi; bu yerda faqat so'roq qo'yiladi. */
  useEffect(() => {
    if (!loading) return;
    const guard = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [loading]);

  /* Kataklar JAVON bo'yicha guruhlanadi: skladchi javon oldiga boradi,
     katakka emas. Bandlik ham javonniki — o'sha javonga yana sig'adimi
     degan savolga javob shu. */
  const racks = useMemo<RackGroup[]>(() => {
    const byCell = new Map<string, { rack: LayoutRack; code: string }>();
    for (const zone of layout.data?.zones ?? []) {
      for (const rack of zone.racks) {
        for (const cell of rack.cells) byCell.set(cell.id, { rack, code: cell.code });
      }
    }

    const groups = new Map<string, RackGroup>();
    for (const item of items) {
      const found = byCell.get(item.cellId);
      if (found === undefined) continue;
      const { rack, code } = found;
      const existing = groups.get(rack.id);
      if (existing === undefined) {
        const used = rack.cells.filter((cell) => cell.status !== 'FREE').length;
        groups.set(rack.id, {
          rackId: rack.id,
          label: rackLabel(rack),
          percent: rack.cells.length === 0 ? 0 : Math.round((used / rack.cells.length) * 100),
          cells: [code],
        });
      } else if (!existing.cells.includes(code)) {
        existing.cells.push(code);
      }
    }
    return [...groups.values()];
  }, [items, layout.data]);

  const totalQty = items.reduce((sum, item) => sum + item.qty, 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      busy={loading}
      title={t('supply.loadTitle')}
      description={t('supply.loadSubtitle', { count: items.length })}
      error={error ?? undefined}
      onSubmit={loading ? undefined : onConfirm}
      footer={
        <>
          <Button variant="secondary" disabled={loading} onClick={onClose}>
            {t('supply.cancel')}
          </Button>
          {/* Eni o'zgarmaydi: spinner matnning YONIDA turadi, o'rniga
              emas — aks holda tugma bosilgan zahoti torayib, sichqoncha
              boshqa element ustida qolardi. */}
          <Button variant="accent" disabled={loading} onClick={onConfirm}>
            {loading ? <Spinner size="sm" /> : <Upload size={16} aria-hidden />}
            {t(loading ? 'supply.loadSubmitting' : 'supply.load')}
          </Button>
        </>
      }
    >
      {/* `min-w-0` — ichidagi hech bir satr tanani kengaytirib, gorizontal
          aylantirish chiqarmasin. Oraliq `gap-4` — boshqa oynalar
          ("Xodimni tizimga qo'shish") bilan bir xil. */}
      <div className="flex min-w-0 flex-col gap-4">
        {/* JAVONLAR — tanlangan kataklar qaysi javonda ekani. */}
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-text">{t('supply.loadRacks')}</h3>
          <ul className="flex flex-col gap-2">
            {racks.map((rack) => (
              /* Ikki qator: tepada javon nomi va bandligi, ostida KATAKLAR.
                 Ilgari kataklar ro'yxati `shrink-0` edi va hech qachon
                 o'ralmasdi — o'nta kod oyna enidan oshib, tana gorizontal
                 aylanardi va oxirgi kodlar kesilib qolardi. Endi ro'yxat
                 butun enni oladi va vergullardan keyin o'raladi. */
              <li
                key={rack.rackId}
                className={cn(
                  'grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1',
                  'rounded-[var(--radius-sm)] border border-border px-3 py-2',
                )}
              >
                <span className="min-w-0 truncate text-sm font-medium text-text" title={rack.label}>
                  {rack.label}
                </span>
                {rack.percent >= CROWDED ? (
                  <Badge tone="warn">
                    <AlertTriangle size={12} aria-hidden />
                    {t('supply.rackOccupancy', { percent: rack.percent })}
                  </Badge>
                ) : (
                  <span className="tabular shrink-0 text-xs text-text-muted">
                    {t('supply.rackOccupancy', { percent: rack.percent })}
                  </span>
                )}
                {/* Har kod BUTUN o'raladi: `A-01-03-` va `04` ikki qatorga
                    bo'linsa, skladchi uni ikki xil katak deb o'qirdi. */}
                <span className="tabular col-span-2 flex min-w-0 flex-wrap gap-x-1.5 text-xs leading-relaxed text-text-muted">
                  {rack.cells.map((code, index) => (
                    <span key={code} className="whitespace-nowrap">
                      {code}
                      {index < rack.cells.length - 1 ? ',' : ''}
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* MAHSULOTLAR — yig'ilgan holda: o'nlab satr oynani cho'zib
            yuborardi va pastdagi jami son ekrandan chiqib ketardi. */}
        <section className="flex flex-col gap-2">
          <button
            type="button"
            aria-expanded={listOpen}
            onClick={() => setListOpen((current) => !current)}
            className="flex items-center gap-1.5 self-start text-sm font-semibold text-text"
          >
            {t('supply.loadItems', { count: items.length })}
            <ChevronDown
              size={16}
              aria-hidden
              className={cn(
                'text-text-muted transition-transform duration-[var(--motion-fast)]',
                'ease-[var(--ease-standard)]',
                listOpen ? 'rotate-180' : undefined,
              )}
            />
          </button>

          {listOpen ? (
            <ul className="flex max-h-[12.5rem] flex-col gap-1 overflow-y-auto overscroll-contain">
              {items.map((item, index) => (
                <li
                  key={`${item.lineId}-${item.cellId}-${index}`}
                  className="flex items-center gap-3 rounded-[var(--radius-sm)] px-2 py-1.5 text-sm odd:bg-surface-2"
                >
                  <span className="tabular shrink-0 text-xs text-text-muted">{item.code}</span>
                  <span className="min-w-0 flex-1 truncate text-text" title={item.name}>
                    {item.name}
                  </span>
                  <span className="tabular shrink-0 text-text">
                    {item.qty} {item.unit}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </section>

        <dl
          className={cn(
            'grid grid-cols-2 gap-2 rounded-[var(--radius-md)] bg-surface-2 p-3 text-sm',
          )}
        >
          <dt className="text-text-muted">{t('supply.lineCount')}</dt>
          <dd className="tabular text-right font-semibold text-text">{items.length}</dd>
          <dt className="text-text-muted">{t('supply.totalQty')}</dt>
          <dd className="tabular text-right font-semibold text-text">
            {Math.round(totalQty * 1000) / 1000}
          </dd>
        </dl>

        <p className="text-xs text-text-muted">{t('supply.finishHint')}</p>
      </div>
    </Dialog>
  );
};
